import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";

/**
 * StatusBadge
 *
 * Canonical reference: ianfiles/ui-design-system.md Appendix B
 *
 * Eleven typed status maps for the entire ERP. Used as:
 *
 *   <StatusBadge status={row.status} type="order" />
 *
 * The four shadcn variants do most of the heavy lifting:
 *   - `secondary` → soft/inert (Draft, Inactive, Not Required, …)
 *   - `destructive` → hard error (Rejected, Cancelled, Exception, …)
 *   - `default` → terminal/solid success (Accepted, Paid, Closed, Fulfilled)
 *   - `outline` → in-progress, paired with `border-*-500 text-*-600
 *     dark:text-*-400` accents for hue.
 *
 * In-progress states use **outline + a coloured rim** rather than a filled
 * colour so the dominant ink on a list page is calm and the only saturated
 * pills are terminal states. This is the single most important rule from
 * Appendix B — don't break it without a good reason.
 *
 * Unknown statuses fall back to a `secondary` pill with the raw key
 * title-cased. Better than rendering nothing — surfaces backend drift.
 */

interface StatusConfig {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  className?: string;
}

export type StatusType =
  | "account"
  | "quote"
  | "order"
  | "purchaseOrder"
  | "invoice"
  | "shipment"
  | "opportunity"
  | "product"
  | "contract"
  | "procurement"
  | "fulfillment";

interface StatusBadgeProps {
  status: string | null | undefined;
  type: StatusType;
  /** Stable test id, defaults to `badge-status-{normalised-label}`. */
  "data-testid"?: string;
  className?: string;
}

// ─── account ────────────────────────────────────────────────────────────────
const accountStatusConfig: Record<string, StatusConfig> = {
  active: { label: "Active", variant: "default", className: "bg-green-600" },
  inactive: { label: "Inactive", variant: "secondary" },
  prospect: { label: "Prospect", variant: "outline" },
};

// ─── quote ──────────────────────────────────────────────────────────────────
const quoteStatusConfig: Record<string, StatusConfig> = {
  draft: { label: "Draft", variant: "secondary" },
  sent: {
    label: "Sent",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  accepted: { label: "Accepted", variant: "default", className: "bg-green-600" },
  converted: {
    label: "Converted",
    variant: "default",
    className: "bg-purple-600",
  },
  rejected: { label: "Rejected", variant: "destructive" },
  expired: { label: "Expired", variant: "secondary" },
};

// ─── order (Sales Order — full 11 states) ───────────────────────────────────
const orderStatusConfig: Record<string, StatusConfig> = {
  pending: { label: "Pending", variant: "secondary" },
  submitted: {
    label: "Submitted",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  confirmed: { label: "Confirmed", variant: "default", className: "bg-blue-600" },
  partially_fulfilled: {
    label: "Partially Fulfilled",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  fulfilled: { label: "Fulfilled", variant: "default", className: "bg-teal-600" },
  partially_shipped: {
    label: "Partially Shipped",
    variant: "outline",
    className: "border-purple-500 text-purple-600 dark:text-purple-400",
  },
  fully_shipped: {
    label: "Fully Shipped",
    variant: "default",
    className: "bg-purple-600",
  },
  partially_delivered: {
    label: "Partially Delivered",
    variant: "outline",
    className: "border-emerald-500 text-emerald-600 dark:text-emerald-400",
  },
  fully_delivered: {
    label: "Fully Delivered",
    variant: "default",
    className: "bg-emerald-600",
  },
  invoiced: { label: "Invoiced", variant: "default", className: "bg-indigo-600" },
  closed: { label: "Closed", variant: "default", className: "bg-green-600" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

// ─── purchaseOrder ──────────────────────────────────────────────────────────
const poStatusConfig: Record<string, StatusConfig> = {
  draft: { label: "Draft", variant: "secondary" },
  submitted: {
    label: "Submitted",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  acknowledged: {
    label: "Acknowledged",
    variant: "default",
    className: "bg-blue-600",
  },
  confirmed: { label: "Confirmed", variant: "default", className: "bg-blue-700" },
  in_production: {
    label: "In Production",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  partially_received: {
    label: "Partially Received",
    variant: "outline",
    className: "border-teal-500 text-teal-600 dark:text-teal-400",
  },
  received: { label: "Received", variant: "default", className: "bg-green-600" },
  closed: { label: "Closed", variant: "default", className: "bg-gray-600" },
  cancelled: { label: "Cancelled", variant: "destructive" },
  issue: { label: "Issue", variant: "destructive" },
};

// ─── invoice ────────────────────────────────────────────────────────────────
const invoiceStatusConfig: Record<string, StatusConfig> = {
  draft: { label: "Draft", variant: "secondary" },
  sent: {
    label: "Sent",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  deposit_requested: {
    label: "Deposit Requested",
    variant: "outline",
    className: "border-teal-500 text-teal-600 dark:text-teal-400",
  },
  overdue: { label: "Overdue", variant: "destructive" },
  partially_paid: {
    label: "Partially Paid",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  paid: { label: "Paid", variant: "default", className: "bg-green-600" },
  voided: { label: "Voided", variant: "secondary", className: "line-through" },
  closed: { label: "Closed", variant: "secondary" },
};

// ─── shipment (Appendix B uses SCREAMING_CASE keys) ─────────────────────────
const shipmentStatusConfig: Record<string, StatusConfig> = {
  PLANNED: { label: "Planned", variant: "secondary" },
  BOOKED: {
    label: "Booked",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  PICKED_UP: {
    label: "Picked Up",
    variant: "outline",
    className: "border-teal-500 text-teal-600 dark:text-teal-400",
  },
  IN_TRANSIT: {
    label: "In Transit",
    variant: "default",
    className: "bg-purple-600",
  },
  AT_PORT_OF_ORIGIN: {
    label: "At Port of Origin",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  ON_WATER: { label: "On Water", variant: "default", className: "bg-blue-600" },
  AT_PORT_OF_DESTINATION: {
    label: "At Port of Dest.",
    variant: "outline",
    className: "border-indigo-500 text-indigo-600 dark:text-indigo-400",
  },
  IN_CUSTOMS: {
    label: "In Customs",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  OUT_FOR_DELIVERY: {
    label: "Out for Delivery",
    variant: "default",
    className: "bg-teal-600",
  },
  DELIVERED: {
    label: "Delivered",
    variant: "default",
    className: "bg-green-600",
  },
  PARTIALLY_DELIVERED: {
    label: "Partially Delivered",
    variant: "outline",
    className: "border-green-500 text-green-600 dark:text-green-400",
  },
  EXCEPTION: { label: "Exception", variant: "destructive" },
  CANCELLED: {
    label: "Cancelled",
    variant: "secondary",
    className: "line-through",
  },
};

// ─── opportunity ────────────────────────────────────────────────────────────
const opportunityStageConfig: Record<string, StatusConfig> = {
  prospecting: { label: "Prospecting", variant: "secondary" },
  qualification: { label: "Qualification", variant: "outline" },
  proposal: {
    label: "Proposal",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  negotiation: {
    label: "Negotiation",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  closed_won: {
    label: "Closed Won",
    variant: "default",
    className: "bg-green-600",
  },
  closed_lost: { label: "Closed Lost", variant: "destructive" },
};

// ─── product class ──────────────────────────────────────────────────────────
const productClassConfig: Record<string, StatusConfig> = {
  stock: { label: "Stock", variant: "default", className: "bg-blue-600" },
  custom: { label: "Custom", variant: "default", className: "bg-purple-600" },
};

// ─── contract ───────────────────────────────────────────────────────────────
const contractStatusConfig: Record<string, StatusConfig> = {
  draft: { label: "Draft", variant: "secondary" },
  active: { label: "Active", variant: "default", className: "bg-green-600" },
  expired: { label: "Expired", variant: "destructive" },
  terminated: { label: "Terminated", variant: "secondary" },
};

// ─── procurement (sub-status on order lines) ────────────────────────────────
const procurementStatusConfig: Record<string, StatusConfig> = {
  not_required: { label: "Not Required", variant: "secondary" },
  pending_po: { label: "Pending PO", variant: "outline" },
  po_submitted: {
    label: "PO Submitted",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  po_confirmed: {
    label: "PO Confirmed",
    variant: "default",
    className: "bg-blue-600",
  },
  in_production: {
    label: "In Production",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  ready_to_ship: {
    label: "Ready to Ship",
    variant: "default",
    className: "bg-teal-600",
  },
  partially_shipped: {
    label: "Partially Shipped",
    variant: "outline",
    className: "border-purple-500 text-purple-600 dark:text-purple-400",
  },
  fully_shipped: {
    label: "Fully Shipped",
    variant: "default",
    className: "bg-green-600",
  },
  issue: { label: "Issue", variant: "destructive" },
};

// ─── fulfillment (sub-status on order lines) ────────────────────────────────
const fulfillmentStatusConfig: Record<string, StatusConfig> = {
  pending: { label: "Pending", variant: "secondary" },
  partial: {
    label: "Partial",
    variant: "outline",
    className: "border-amber-500 text-amber-600 dark:text-amber-400",
  },
  allocated: {
    label: "Allocated",
    variant: "outline",
    className: "border-blue-500 text-blue-600 dark:text-blue-400",
  },
  fulfilled: {
    label: "Fulfilled",
    variant: "default",
    className: "bg-green-600",
  },
};

const configMap: Record<StatusType, Record<string, StatusConfig>> = {
  account: accountStatusConfig,
  quote: quoteStatusConfig,
  order: orderStatusConfig,
  purchaseOrder: poStatusConfig,
  invoice: invoiceStatusConfig,
  shipment: shipmentStatusConfig,
  opportunity: opportunityStageConfig,
  product: productClassConfig,
  contract: contractStatusConfig,
  procurement: procurementStatusConfig,
  fulfillment: fulfillmentStatusConfig,
};

/** Title-case an unknown enum key like `partially_shipped` → `Partially Shipped`. */
function titleCase(key: string): string {
  return key
    .split(/[_\s]/g)
    .map((part) =>
      part.length === 0 ? "" : part[0].toUpperCase() + part.slice(1)
    )
    .join(" ");
}

export function StatusBadge({
  status,
  type,
  "data-testid": dataTestId,
  className,
}: StatusBadgeProps) {
  if (!status) return null;

  const map = configMap[type];
  const config: StatusConfig =
    map?.[status] ?? {
      label: titleCase(status),
      variant: "secondary",
    };

  const testId =
    dataTestId ?? `badge-status-${config.label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <Badge
      variant={config.variant}
      data-testid={testId}
      className={cn("text-xs", config.className, className)}
    >
      {config.label}
    </Badge>
  );
}
